import { useState, useEffect } from 'react';
import type { CustomComponents } from 'react-day-picker';
import {
  Calendar,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type CalendarProps,
} from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';
import { TimeColumn } from './TimeColumn';
import {
  ensureFutureDate,
  get12HourFormat,
  getAmPm,
  setHour,
  createDefaultTimeForDate,
  isInPast,
} from '../utils/dateTime';

interface DateTimePickerProps {
  date: Date | null;
  onDateChange: (date: Date | null) => void;
  disablePastDates?: boolean;
  dayButton?: CustomComponents['DayButton'];
  modifiers?: Record<string, any>;
}

export function DateTimePicker({
  date,
  onDateChange,
  disablePastDates = true,
  dayButton,
  modifiers = {},
}: DateTimePickerProps) {
  const { t } = useTranslation('common');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<string | null>(null);
  const [selectedAmPm, setSelectedAmPm] = useState<string | null>(null);

  // Update selected time values when date changes
  useEffect(() => {
    if (date) {
      setSelectedHour(get12HourFormat(date).toString());
      setSelectedMinute(date.getMinutes().toString().padStart(2, '0'));
      setSelectedAmPm(getAmPm(date));
    } else {
      // Clear selections when date is null
      setSelectedHour(null);
      setSelectedMinute(null);
      setSelectedAmPm(null);
    }
  }, [date]);

  const handleTimeChange = (type: 'hour' | 'minute' | 'ampm', value: string) => {
    if (type === 'hour') {
      setSelectedHour(value);
    } else if (type === 'minute') {
      setSelectedMinute(value);
    } else if (type === 'ampm') {
      setSelectedAmPm(value);
    }

    // If no date is selected, don't update the date
    if (!date) return;

    let newDate = new Date(date);

    // Apply the requested change
    if (type === 'hour') {
      const hour = parseInt(value, 10);
      const ampm = getAmPm(newDate);
      newDate = setHour(newDate, hour, ampm);
    } else if (type === 'minute') {
      newDate.setMinutes(parseInt(value, 10));
    } else if (type === 'ampm') {
      const hour = get12HourFormat(newDate);
      newDate = setHour(newDate, hour, value as 'AM' | 'PM');
    }

    // Ensure the date is in the future if needed
    if (disablePastDates) {
      newDate = ensureFutureDate(newDate);
    }

    onDateChange(newDate);
  };

  // Check if a time option should be disabled
  const isTimeDisabled = (type: 'hour' | 'minute' | 'ampm', value: string): boolean => {
    if (!date || !disablePastDates) return false;

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (!isToday) return false; // Only disable times for today

    const hour = date.getHours();

    if (type === 'hour') {
      const hourValue = parseInt(value, 10);
      const isPM = hour >= 12;
      const compareHour = isPM ? (hourValue === 12 ? 12 : hourValue + 12) : hourValue === 12 ? 0 : hourValue;
      return compareHour < now.getHours();
    }

    if (type === 'minute') {
      const minuteValue = parseInt(value, 10);
      return hour === now.getHours() && minuteValue < now.getMinutes();
    }

    if (type === 'ampm') {
      return value === 'AM' && now.getHours() >= 12 && hour < 12;
    }

    return false;
  };

  // Hours, minutes and AM/PM values
  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
  const amPmValues = ['AM', 'PM'];

  // Set up disabled dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const disabledDays = disablePastDates ? { before: today } : undefined;
  const allModifiers = { ...modifiers, ...(disabledDays ? { disabled: disabledDays } : {}) };

  // Handle calendar date selection
  const handleDateSelect = (newDate: Date | undefined) => {
    if (!newDate) {
      onDateChange(null);
      return;
    }

    const selectedDate = new Date(newDate);

    if (date) {
      // Keep existing time if already set
      selectedDate.setHours(date.getHours(), date.getMinutes());
    } else {
      // Set default time for the selected date
      const defaultTime = createDefaultTimeForDate(selectedDate, disablePastDates);
      selectedDate.setHours(defaultTime.getHours(), defaultTime.getMinutes());
    }

    // Final check to ensure the selected time is in the future if needed
    if (disablePastDates && isInPast(selectedDate)) {
      const adjustedDate = ensureFutureDate(selectedDate);
      selectedDate.setHours(adjustedDate.getHours(), adjustedDate.getMinutes());
    }

    onDateChange(selectedDate);
  };

  // Calendar props with proper typing
  const baseCalendarProps: CalendarProps = {
    mode: 'single' as const,
    selected: date || undefined,
    onSelect: handleDateSelect,
    month: selectedMonth,
    onMonthChange: setSelectedMonth,
    modifiers: allModifiers,
    disabled: disabledDays,
  };

  // Conditionally add components prop if dayButton is provided
  const calendarProps: CalendarProps = dayButton
    ? { ...baseCalendarProps, components: { DayButton: dayButton } }
    : baseCalendarProps;

  return (
    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
      <div className="relative border rounded-md flex justify-center items-center">
        <Calendar {...calendarProps} />
      </div>

      {/* Time selector */}
      <div className="hidden md:flex flex-col h-[300px] divide-y border rounded-md">
        <div className="flex h-full divide-x">
          {/* Hours */}
          <TimeColumn
            values={hours}
            selectedValue={selectedHour || ''}
            onChange={(value) => handleTimeChange('hour', value)}
            isDisabled={(value) => isTimeDisabled('hour', value)}
          />

          {/* Minutes */}
          <TimeColumn
            values={minutes}
            selectedValue={selectedMinute || ''}
            onChange={(value) => handleTimeChange('minute', value)}
            isDisabled={(value) => isTimeDisabled('minute', value)}
          />

          {/* AM/PM */}
          <TimeColumn
            values={amPmValues}
            selectedValue={selectedAmPm || ''}
            onChange={(value) => handleTimeChange('ampm', value)}
            isDisabled={(value) => isTimeDisabled('ampm', value)}
          />
        </div>
      </div>

      {/* Mobile Time selector */}
      <div className="sm:hidden grid grid-cols-3 gap-2">
        {/* Hour Select */}
        <Select value={selectedHour || undefined} onValueChange={(value) => handleTimeChange('hour', value)}>
          <SelectTrigger>
            <SelectValue placeholder={t('date-time.hour')} />
          </SelectTrigger>
          <SelectContent>
            {hours.map((hour) => (
              <SelectItem key={hour} value={hour} disabled={isTimeDisabled('hour', hour)}>
                {hour}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Minute Select */}
        <Select value={selectedMinute || undefined} onValueChange={(value) => handleTimeChange('minute', value)}>
          <SelectTrigger>
            <SelectValue placeholder={t('date-time.minute')} />
          </SelectTrigger>
          <SelectContent>
            {minutes.map((minute) => (
              <SelectItem key={minute} value={minute} disabled={isTimeDisabled('minute', minute)}>
                {minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* AM/PM Select */}
        <Select value={selectedAmPm || undefined} onValueChange={(value) => handleTimeChange('ampm', value)}>
          <SelectTrigger>
            <SelectValue placeholder={t('date-time.am-pm')} />
          </SelectTrigger>
          <SelectContent>
            {amPmValues.map((ampm) => (
              <SelectItem key={ampm} value={ampm} disabled={isTimeDisabled('ampm', ampm)}>
                {ampm}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
